import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../api/client'

interface CurrencyPreferences { standard_currency: string; currency_mode: 'standard' | 'native' }
const commonCurrencies = ['USD', 'EUR', 'JPY', 'KRW', 'GBP', 'CHF', 'CAD', 'AUD', 'CNY', 'HKD', 'SGD', 'TWD', 'INR', 'SEK', 'NOK', 'DKK', 'NZD', 'BRL', 'ZAR']

export function currencyOptions(...preferred: Array<string | null | undefined>) {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('currency') ?? []
  return [...new Set([...preferred.filter((code): code is string => Boolean(code)), ...commonCurrencies, ...supported])]
}

export function useResearchCurrency() {
  const client = useQueryClient()
  const currencies = currencyOptions()
  const preferences = useQuery({ queryKey: ['currency-preferences'], queryFn: () => apiRequest<CurrencyPreferences>('/api/settings/currency'), staleTime: 60_000 })
  const settings = preferences.data ?? { standard_currency: 'USD', currency_mode: 'native' as const }
  const currency = settings.currency_mode === 'standard' ? settings.standard_currency : 'native'
  const save = useMutation({
    mutationFn: (value: string) => apiRequest<CurrencyPreferences>('/api/settings/currency', { method: 'PUT', body: JSON.stringify({ standard_currency: value === 'native' ? settings.standard_currency : value, currency_mode: value === 'native' ? 'native' : 'standard' }) }),
    onSuccess: data => { client.setQueryData(['currency-preferences'], data) },
  })
  const control = <label className="research-currency">Currency <select className="select select--small" aria-label="Research currency" value={currency} disabled={save.isPending} onChange={event => save.mutate(event.target.value)}>
    <option value="native">Native currency</option>
    {[...new Set([settings.standard_currency, ...currencies])].map(code => <option key={code} value={code}>{code} · standard</option>)}
  </select>{save.isError && <span role="alert">Could not save currency</span>}</label>
  return { currency, control, ready: !preferences.isLoading }
}

export function countryLabel(code?: string | null) {
  const normalized = code?.trim().toUpperCase()
  if (!normalized || !/^[A-Z]{2}$/.test(normalized)) return 'Country unknown'
  return new Intl.DisplayNames(['en'], { type: 'region' }).of(normalized) ?? normalized
}
